import Handlebars from 'handlebars';

import Page from '../page';
import ProductData from '../../model/product-data';
import HTMLTemplateHelper from '../../helpers/html-template-helper';
import QuantitySelector from '../../components/inputs/quantity-selector';
import AsyncQuickAdd from '../../components/orders/async-quick-add';
import MessageHelper from '../../helpers/message-helper';
import AsyncProductData from '../../components/product/async-product-data';

export default abstract class AbstractTemplateDetailsPage extends Page {
  protected readonly form: HTMLFormElement;
  protected readonly entriesContainer: HTMLElement;
  protected readonly removeAllButton: HTMLButtonElement | null;

  protected readonly rowTemplate: Handlebars.TemplateDelegate;

  protected readonly netTotalSpan: HTMLSpanElement;
  protected readonly taxTotalSpan: HTMLSpanElement;
  protected readonly grandTotalSpan: HTMLSpanElement;

  protected constructor(protected container: HTMLElement, recalculatePrice = true) {
    super();

    this.form = container.querySelector('.js-templateForm') as HTMLFormElement;
    this.entriesContainer = container.querySelector('.js-entries') as HTMLElement;
    this.removeAllButton = container.querySelector('.js-removeAll');

    Array.from(container.querySelectorAll<HTMLDivElement>('.js-quick-add-form'))
      .forEach((quickAddElement) => new AsyncQuickAdd($(quickAddElement), (product) => this.addRow(product)));

    this.netTotalSpan = container.querySelector('.js-netTotal') as HTMLSpanElement;
    this.taxTotalSpan = container.querySelector('.js-taxTotal') as HTMLSpanElement;
    this.grandTotalSpan = container.querySelector('.js-grandTotal') as HTMLSpanElement;

    const rowTemplateContainer = container.querySelector('.js-entryTemplate');
    if (!rowTemplateContainer) {
      throw new Error('No entry template found');
    }
    this.rowTemplate = Handlebars.compile(rowTemplateContainer.innerHTML);

    this.bindValidateSubmit();
    this.bindRecalculateAsyncPrice();

    if (this.removeAllButton) {
      this.bindRemoveAllRows();
    }
    Array.from(this.entriesContainer.children).forEach((row) => this.bindRowEventListeners(row as HTMLTableRowElement));
    if (recalculatePrice) {
      this.recalculatePrice();
    }
  }

  protected bindValidateSubmit(): void {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  protected handleSubmit(e: Event): void {
    if (this.form.dataset.blockSubmit) {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
    }

    if (!e.defaultPrevented) {
      window.NotificationHelper.clearNotifications();
      if (this.entriesContainer.children.length === 0) {
        window.NotificationHelper.showError(MessageHelper.getMessage('eparts.template.entriesRequired'));
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }

  protected bindRemoveAllRows(): void {
    this.removeAllButton?.addEventListener('click', () => this.removeAllRows());
  }

  protected bindRowEventListeners(row: HTMLTableRowElement): void {
    const $row = $(row);
    QuantitySelector.init($row.find('.js-qty-selector'));

    const priceChangeHandler = () => this.recalculatePrice();
    const priceInputs = Array.from(row.querySelectorAll<HTMLInputElement>(this.getPriceInputSelector()));
    priceInputs.forEach((input) => input.addEventListener('input', priceChangeHandler));

    const removeButton = row.querySelector('.js-removeEntry');
    removeButton?.addEventListener('click', () => this.removeRow(row));
  }

  protected bindRecalculateAsyncPrice(): void {
    if (!this.isOrdered()) {
      this.container.addEventListener(AsyncProductData.DATA_LOADED_EVENT, () => this.recalculatePrice());
    }
  }

  protected isOrdered(): boolean {
    return this.container.dataset.isOrdered === 'true';
  }

  protected abstract getPriceInputSelector(): string;

  protected handleRowChange(): void {
    this.recalculatePrice();
  }

  protected removeAllRows(): void {
    Array.from(this.entriesContainer.children).forEach((row) => row.remove());
    this.handleRowChange();
  }

  protected removeRow(row: HTMLTableRowElement): void {
    row.remove();
    this.reindexRows();
    this.handleRowChange();
  }

  protected addRow(product?: ProductData): void {
    if (product && product.sapBlocked && !product.hasSubstitution) {
      window.NotificationHelper.showError(MessageHelper.getMessage('text.quickOrder.product.not.found'));
      return;
    }

    const row = this.createRow(this.entriesContainer.children.length, product);
    this.entriesContainer.append(row);
    this.bindRowEventListeners(row);
    this.handleRowChange();
  }

  protected createRow(index: number, product?: ProductData): HTMLTableRowElement {
    const newRowHTML = this.rowTemplate({
      index,
      product,
    });
    return HTMLTemplateHelper.createFromString(newRowHTML) as HTMLTableRowElement;
  }

  protected reindexRows(): void {
    const indexRegex = /\[\d+]/g;
    Array.from(this.entriesContainer.children).forEach((row, index) => {
      Array.from(row.querySelectorAll<HTMLInputElement>('input'))
        .forEach((input) => {
          // eslint-disable-next-line no-param-reassign
          input.name = input.name.replace(indexRegex, `[${index}]`);
        });
    });
  }

  protected abstract recalculatePrice(): void;
}
